/**
 * FieldMaterialIssuesChip — Phase 4 read-only chip.
 * Surfaces unresolved field material feedback counts on procurement /
 * job-detail / install-today rows. No mutation imports.
 */
import type { Job } from "@/data/types";
import { useFieldMaterialFeedback } from "@/data/procurement/useFieldMaterialFeedback";
import { Badge } from "@/components/ui/badge";
import { Hammer, AlertTriangle, XCircle } from "lucide-react";
import { cn } from "@/lib/utils";

interface Props {
  job: Job;
  className?: string;
  /** Hide entirely when no unresolved events exist. Default true. */
  hideWhenSilent?: boolean;
}

export function FieldMaterialIssuesChip({ job, className, hideWhenSilent = true }: Props) {
  const fb = useFieldMaterialFeedback(job.id);
  if (hideWhenSilent && fb.unresolvedCount === 0) return null;
  const blocker = fb.hasUnresolvedBlocker;
  const warn = !blocker && fb.hasUnresolvedWarn;
  const tone = blocker
    ? "bg-destructive/10 text-destructive ring-1 ring-destructive/30 border-destructive/30"
    : warn
      ? "bg-warning/10 text-warning ring-1 ring-warning/30 border-warning/30"
      : "bg-muted/40 text-muted-foreground ring-1 ring-border/60 border-border/60";
  const Icon = blocker ? XCircle : warn ? AlertTriangle : Hammer;
  const label = blocker
    ? `Field blocker · ${fb.unresolvedBlockers.length}`
    : warn
      ? `Field warning · ${fb.unresolvedWarns.length}`
      : `Field note · ${fb.unresolvedCount}`;
  return (
    <Badge
      variant="outline"
      className={cn("gap-1 text-[10px] font-medium", tone, className)}
      title={label}
      data-testid="field-material-issues-chip"
      data-unresolved={fb.unresolvedCount}
      data-has-blocker={blocker ? "true" : "false"}
    >
      <Icon className="h-3.5 w-3.5" />
      <span className="truncate max-w-[180px]">{label}</span>
    </Badge>
  );
}
